import { Injectable, inject } from '@angular/core';
import { GqlService, sharedAdminQueries } from '@tgx/shared/data-access/graphql';
import { addMemberUser } from '../operations/mutations/add-member';
import { removeMemberUser } from '../operations/mutations/remove-member';
import { MemberInfo, Members } from '@tgx/shared/interfaces';
import { MembersInfo } from '../interfaces/member-info';
import { updateMemberDataInOrganization } from '../operations/mutations/update-member-in-organization';
import { MembersRole } from '@tgx/shared/enums';
import { BehaviorSubject } from 'rxjs';
import { UsersManagementFilters } from '../interfaces/users-management-filters';
import { MappedMemberRole } from '../constants/users-permissions.constants';
import { changeOrganizationMemberOwner } from '../operations/mutations/change-organization-member-owner';
import { ErrorMessageHandler, getUserName } from '@tgx/shared/utils';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private gqlService = inject(GqlService);

  allMembers = new BehaviorSubject<MemberInfo[]>(null);
  private membersRequestId = 0;
  private loadedMembersOrgCode: string;
  private filtersSubject = new BehaviorSubject<UsersManagementFilters | null>(null);
  filters$ = this.filtersSubject.asObservable();

  get currentMembersOrgCode(): string {
    return this.loadedMembersOrgCode;
  }

  updateMemberRole(orgCode: string, role: any, email: string): Promise<Members> {
    return new Promise((resolve, reject) => {
      this.gqlService
        .mutationGateway(updateMemberDataInOrganization, {
          where: { member: email, organization: orgCode },
          data: { role: role },
        })
        .then((response: any) => {
          if (response) {
            resolve(response['admin']['updateMemberDataInOrganization']);
          } else if (response['admin']['updateMemberDataInOrganization']['adviseMessage']) {
            reject(response['admin']['updateMemberDataInOrganization']['adviseMessage']);
          } else {
            reject('The role has not been updated.');
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async addMemberToOrganization(organization: string, member: string, role: string): Promise<string> {
    return await new Promise((resolve, reject) => {
      this.gqlService
        .mutationGateway(addMemberUser, { where: { member, organization }, data: { role } })
        .then((response: any) => {
          const errMsg = 'There is a problem processing the IAM response';
          if (response['admin']['addMemberToOrganization']['code']) {
            resolve(response['admin']['addMemberToOrganization']['code']);
          } else if (response['admin']['addMemberToOrganization']['adviseMessage']) {
            reject(response['admin']['addMemberToOrganization']['adviseMessage']['description'] ?? errMsg);
          } else {
            reject(errMsg);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async removeUserFromOrg(orgCode: string, user: MemberInfo): Promise<MemberInfo> {
    return await new Promise((resolve, reject) => {
      this.gqlService
        .mutationGateway(removeMemberUser, { input: { member: user.email, organization: orgCode } })
        .then((response: any) => {
          if (response.admin.removeMemberFromOrganization.adviseMessage === null) {
            resolve(user);
          } else {
            reject(response['admin']['removeMemberFromOrganization']['adviseMessage']);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  // New User Management Section END

  async getAllMembersByOrgV2(code_in: string): Promise<MembersInfo[] | null> {
    const body = {
      org: [code_in],
    };

    try {
      const response = await this.gqlService.queryGateway(sharedAdminQueries.getAllMembersProfilesByOrg, body);
      return response['admin']['allMembers']['edges'] || [];
    } catch {
      return null;
    }
  }

  getName(member: MemberInfo) {
    if (member) {
      return getUserName(member);
    }

    return undefined;
  }

  async retrieveMembersByOrg(orgCode: string): Promise<boolean> {
    const requestId = ++this.membersRequestId;

    // Retrieve members from IAM
    const allMembersFromOrg = await this.getAllMembersByOrgV2(orgCode);
    if (requestId !== this.membersRequestId) return false;

    if (!allMembersFromOrg) {
      this.loadedMembersOrgCode = null;
      this.allMembers.next(null);
      return false;
    }

    this.loadedMembersOrgCode = orgCode;
    if (allMembersFromOrg.length === 0) {
      this.allMembers.next([]);
      return true;
    } else this.allMembers.next(null);

    if (allMembersFromOrg?.length > 0) {
      const membersList: MemberInfo[] = [];

      allMembersFromOrg.forEach((member) => {
        const { code: memberCode, firstName, lastName, organizations } = member.node.membersData;
        const orgData = organizations?.find((org) => org.organization.code === orgCode);
        const serviceRoles: Partial<MemberInfo> = {};
        orgData?.services?.forEach(({ service, role }) => {
          const memberRoleProperty = MappedMemberRole[service] as keyof MemberInfo;
          if (memberRoleProperty) {
            serviceRoles[memberRoleProperty] = role as MembersRole;
          }
        });
        const memberData = {
          email: memberCode,
          role: orgData?.role,
          firstName,
          lastName,
          jobTitle: orgData?.jobTitle,
          ...serviceRoles,
          fullName: '',
        };
        memberData.fullName = this.getName(memberData);

        membersList.push(memberData);
      });
      this.allMembers.next(membersList);
    }

    return true;
  }

  public setFiltersUsers(filters: UsersManagementFilters) {
    this.filtersSubject.next(filters);
  }

  public async changeOrganizationMemberOwner(member: string, orgCode: string): Promise<string> {
    const res: any = await this.gqlService.mutationGateway(changeOrganizationMemberOwner, { member, orgCode });

    ErrorMessageHandler.handleAdviseMessage({
      response: res,
      pathOrObject: res?.admin?.changeOrganizationMemberOwner,
    });

    const changedOwner = res?.admin?.changeOrganizationMemberOwner;
    const userEmail = changedOwner?.code;

    return userEmail;
  }
}
